/**
 * SoloCard — блок соло-тарифа: view (крупная фикс. цена) и edit (правка
 * цены → «Сохранить как новую версию»). Черновик живёт локально в редакторе
 * (сброс при повторном входе — редактор монтируется заново).
 */

import {useState} from 'react';
import {User} from 'lucide-react';

import type {SoloTariff} from '@services/tariffs';
import {Button} from '@components/ui/Button';
import {formatNumber} from '@utils/format';

import {tariffVersionLine} from '../Tariffs.utils';
import {TariffSection} from '../TariffSection';

type SoloCardProps = {
  solo: SoloTariff;
  editing: boolean;
  onEdit: () => void;
  onCancel: () => void;
  onAskSave: (price: number) => void;
};

export const SoloCard = ({solo, editing, onEdit, onCancel, onAskSave}: SoloCardProps) => (
  <TariffSection
    icon={<User className="size-[23px]" strokeWidth={1.7} />}
    iconClassName="bg-accent text-primary"
    title="Соло-врач"
    subtitle="Фиксированная цена в месяц"
    version={tariffVersionLine(solo.version, solo.updatedAt)}
    action={
      editing ? undefined : (
        <Button variant="outline" size="sm" className="h-9 px-3.5" onClick={onEdit}>
          Изменить
        </Button>
      )
    }
  >
    {editing ? (
      <SoloEditor solo={solo} onCancel={onCancel} onAskSave={onAskSave} />
    ) : (
      <div className="mt-5 flex items-baseline gap-2">
        <span className="font-mono text-[38px] font-semibold -tracking-[.02em]">
          {formatNumber(solo.price)}
        </span>
        <span className="text-[15px] text-muted-foreground">сом / месяц</span>
      </div>
    )}
  </TariffSection>
);

const SoloEditor = ({
  solo,
  onCancel,
  onAskSave,
}: {
  solo: SoloTariff;
  onCancel: () => void;
  onAskSave: (price: number) => void;
}) => {
  const [price, setPrice] = useState(String(solo.price));
  const value = Number(price || 0);

  return (
    <div className="mt-5 rounded-xl border border-skr-line2 bg-skr-head px-[18px] py-4">
      <label className="text-[12.5px] font-semibold text-skr-ink2">Цена в месяц, сом</label>
      <div className="mt-2 flex items-center gap-3">
        <input
          value={price}
          onChange={(e) => setPrice(e.target.value.replace(/\D/g, ''))}
          inputMode="numeric"
          className="h-[46px] w-[180px] rounded-[10px] border border-input bg-card px-3.5 font-mono text-lg font-semibold outline-none focus:border-primary focus:ring-[3px] focus:ring-primary/12"
        />
        <span className="text-[13px] text-muted-foreground">сом / месяц</span>
      </div>
      <div className="mt-[18px] flex gap-2.5">
        <Button variant="outline" className="h-[42px] px-4" onClick={onCancel}>
          Отмена
        </Button>
        <Button className="h-[42px] px-4" disabled={value <= 0} onClick={() => onAskSave(value)}>
          Сохранить как новую версию
        </Button>
      </div>
    </div>
  );
};
